declare global {
  interface Window {
    _pxOnCaptchaSuccess: (isValid: boolean) => void;
  }
}

export const botDetectionServiceUiConstants = {
  apiKey: {
    prod: 'PX6yz0QMxV',
    nonProd: 'PXzVPXa9Qu',
  },
};

export type BotDetectionAppearance = {
  challenge?: {
    translation?: {
      [languageCode: string]: {
        btn?: string; // Press and Hold button
        failed?: string; // Message shown in case of failure in previous solve attempt
        ac_1?: string; // Accessibility - Push and Hold for verification
        ac_2?: string; // Accessibility - "Challenge Ended"
        al_1?: string; // Alert on internet connection issue
        al_2?: string; // Alert on browser issue
        btn_done?: string; //Done
      };
    };
    view?: {
      preset?: number; // available options: 0 (classic button), 1 (new design)
      width?: number | string; //e.g: 100, '100%'
      height?: number | string; //e.g: 80, '99%'
      padding?: string; //e.g "5px 1em 0 2em"
      margin?: string; //e.g "1px 1px 0 0"
      parts?: number; //e.g 300 (default - 150)
      css?: string[]; //e.g ["https://fonts.googleapis.com/css?family=Acme&display=swap"]
      backgroundColor?: string; //e.g "#FF0033"
      fillColor?: string; //e.g "#FF0033"
      borderColor?: string; //e.g "#FF0033"
      borderWidth?: number; //e.g 7
      borderRadius?: number; //e.g 10
      buttonBorderWidthOnFocus?: number; //e.g 10
      textColor?: string; //e.g "#FF33DD"
      texSize?: number; //e.g 15
      textFont?: string; // "Acme"
      fontWeight?: number | string; //example: 100, 'bold'
      animation?: boolean; //e.g. true
      checkmarkThickness?: string; //e.g. "4px"
      checkmarkHeight?: string; //e.g. "20px"
      checkmarkWidth?: string; //e.g. "8px"
      targetColor?: string; //e.g. "#FF00FF"
      textTransform?: string; //e.g. "none"
    };
    context?: {
      // mainly relevant for new block page and modal (Auto-ABR)
      logoImgSrc?: string; //e.g: 'https://awsmp-logos.s3.amazonaws.com/22586a6c-27be-426c-b655-bb9783786286/b550252a8d7a14aebef9938beaaccd35.png' - Note: this option replaces the header by default, so if you want both then make sure to explicitly include both the logoImgSrc and headerText options in your customization object.
      fontLinks?: string[]; //e.g: ['https://fonts.googleapis.com/css2?family=Oswald:wght@200;700&display=swap', 'https://fonts.googleapis.com/css2?family=Edu+TAS+Beginner:wght@400;700&display=swap']
      headerText?: string; //e.g: 'Human verification'
      headerColor?: string; //e.g: '#FF0000'
      headerFontSize?: string; //e.g: '30px'
      headerFontFamily?: string; //e.g: 'Oswald',
      headerFontWeight?: string; //e.g: '700',
      messageText?: string; //e.g: 'Please verify you are a human'
      messageColor?: string; //e.g: '#0000FF'
      messageFontSize?: string; //e.g: '20px'
      messageFontFamily?: string; //e.g: 'Edu TAS Beginner',
      messageFontWeight?: string; //e.g: '400',
      failed?: {
        // custom options for the “Please try again” message
        fontSize?: string; //e.g: '14px'
        color?: string; //e.g: '#888888'
      };
    };
  };
};

export type BotDetectionUtils = {
  loadComplete: Promise<void>;
  setChallengeAppearance: (appearance: BotDetectionAppearance) => void;
  setCaptchaResponseHandler: (handler: (isValid: boolean) => void) => void;
  getHeaders(): { [key: string]: string };
};

export const tryInitBotDetection = (log: (str: string, data: object) => void, appId: string) => {
  try {
    return initBotDetection(appId);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    log('Error while initializing bot detection', { error: String(error), errorMessage: message });
  }
};

export const initBotDetection = (appId: string): BotDetectionUtils => {
  const setChallengeAppearance = (appearance: BotDetectionAppearance) => {
    // @ts-ignore
    window['_' + appId] = appearance;
  };

  const setCaptchaResponseHandler = createCaptchaResponseHander();

  const loadComplete = createInitScriptTag(appId);

  const getHeaders = createGetHeaders();

  return {
    loadComplete,
    setCaptchaResponseHandler,
    getHeaders,
    setChallengeAppearance,
  };
};

const createCaptchaResponseHander = () => {
  let captchaResponseHandler: (isValid: boolean) => void = () => console.error('No Captcha response handler set');

  window._pxOnCaptchaSuccess = function (isValid) {
    captchaResponseHandler(isValid);
  };

  return (handler: (isValid: boolean) => void) => {
    captchaResponseHandler = handler;
  };
};

const createInitScriptTag = (appId: string): Promise<void> => {
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript === undefined || firstScript === null || firstScript.parentNode === null) {
    throw new Error('Failed to find first script element');
  }
  const parentNode = firstScript.parentNode;
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', (event) => {
      reject(`Error loading human bot detection script: ${event.type}: ${event.message}`);
    });
    script.async = true;
    script.src = `//client.px-cloud.net/${appId}/main.min.js`;
    parentNode.insertBefore(script, firstScript);
  });
};

const createGetHeaders = () => {
  // Configuration
  const cookiesToSync = ['_px2', '_px3', '_pxhd', '_pxvid', 'pxcts'];

  const getCookieHeader = (): string => {
    return document.cookie
      .split(/;\s?/)
      .reduce((accumulator, cookie) => {
        const cookieName = cookie.split('=')[0];
        cookiesToSync.indexOf(cookieName) > -1 && (accumulator += cookie + '; ');
        return accumulator;
      }, '')
      .slice(0, -2);
  };

  return (): { [key: string]: string } => ({
    'x-px-cookies': getCookieHeader(),
  });
};

export const botDetectionChallengeAppearance: BotDetectionAppearance = {
  challenge: {
    context: {
      logoImgSrc: '/insurance/auto/olb/orbit-logo.png',

      headerText: 'Security check',
      headerFontSize: '24px',
      headerColor: '#404040',
      headerFontWeight: '700',

      messageText:
        'To verify that you are a human, please press and hold to continue. For accessibility help, click the blue button with an icon.',
      messageFontSize: '18px',
      messageColor: '#000000',
      messageFontWeight: '400',
    },
    translation: {
      default: {
        btn: 'Press & hold',
      },
    },
    view: {
      fillColor: '#3964b1',
      backgroundColor: '#ffffff',
      borderColor: '#1C4DA6',
      borderRadius: 50,
      preset: 1,
      borderWidth: 1,
      textColor: '#1C4DA6',
      texSize: 16,
      targetColor: '#ffffff',
      css: ['https://fonts.googleapis.com/css?family=Roboto:400,500&display=swap'],
    },
  },
};
